import moment from 'moment'
// import filters from '@/config/filters'
import util from '@/util'

/**
 * Call PDF builder.
 *
 * @author Alejandro Sanchez <asanchez@claruscare.com>
 */
export default class CallPDF {
	/**
	 * Create a new CallPDF instance.
	 *
	 * @param {Builder} builder
	 */
	constructor(builder) {
		this.call = {}
		this.pdf = builder
	}

	/**
	 * Export the given call to a PDF.
	 *
	 * @param {Object} call
	 * @return {void}
	 */
	make(call) {
		this.call = call
		this._setBaseStyles()

		this._addHeaderColumns()
			._addEmptyLine()
			._addTimeAndPatientInfoTable()
			._addEmptyLine(4)
			._addNotesTable()
			._addEmptyLine(7)
			._addHistoryTable()
			._download()
	}

	/**
	 * Add the dismissal reason to the pdf file.
	 *
	 * @return {CallPDF}
	 */
	_addDismissalReason() {
		const reason = this.pdf.textWrapper('body')

		reason.text('Dismissal reason: ', 'label')
		reason.text(this.call.completion_message || 'Unavailable')

		return this
	}

	/**
	 * Add the PDF's header columns.
	 *
	 * @return {CallPDF}
	 */
	_addHeaderColumns() {
		const columns = this.pdf.addColumns()

		columns
			.image()
			.setWidth(150)
			.setHeight(50)

		const wrapper = columns.textWrapper()

		wrapper.text('Message Details\n', 'header')

		// wrapper.text(moment().format('MMMM Do YYYY h:mm a'), 'label')
		wrapper.text('\n')
		wrapper.text(
			`Exported: ${moment().format('MM/DD/YYYY hh:mm A')}`,
			'headerExportLabel'
		)

		wrapper.addStyles().right()
		wrapper.addStyles().margin([210, 0, 0, 0])
		return this
	}

	/**
	 * Add empty line to the pdf file.
	 *
	 * @param {Number} spacing
	 * @return {CallPDF}
	 */
	_addEmptyLine(spacing = 15) {
		this.pdf
			.text()
			.addStyles()
			.margin([0, spacing])

		return this
	}

	/**
	 * Add history table to the pdf file.
	 *
	 * @return {CallPDF}
	 */
	_addHistoryTable() {
		this.pdf
			.text('Activity History:')
			.addStyles()
			.margin([0, 0, 0, 10])
			.bold()
		// .italics()

		const table = this.pdf.table([120, '*'])

		const headers = table.addRow()

		headers.text('Date', 'label')
		headers.text('Activity', 'label')

		if (!this.call?.events) {
			return this
		}

		const events = [...this.call.events]

		events
			.sort((a, b) => b.id - a.id)
			.forEach(events => {
				let row = table.addRow()

				const timestamp = moment
					.utc(events.created_at)
					.local()
					.format('MM/DD/YYYY hh:mm A')

				row.text(timestamp, 'tableText')
				row.text(events.summary, 'tableText')
			})

		return this
	}

	/**
	 * Add notes table to the pdf file.
	 *
	 * @return {CallPDF}
	 */
	_addNotesTable() {
		this.pdf
			.text('Notes:')
			.addStyles()
			.margin([0, 0, 0, 10])
			.bold()
		// .italics()

		const table = this.pdf.table([120, 120, '*'])

		const header = table.addRow()

		header.text('Date', 'label')
		header.text('Created By', 'label')
		header.text('Note', 'label')

		const notes = [...(this.call.notes || [])]

		notes
			.sort((a, b) => b.id - a.id)
			.forEach(note => {
				const row = table.addRow()

				const timestamp = moment
					.utc(note.created_at)
					.local()
					.format('MM/DD/YYYY hh:mm A')

				row.text(timestamp, 'tableText')
				row.text(
					note.user
						? `${note.user.first_name} ${note.user.last_name}`.trim()
						: 'Unavailable',
					'tableText'
				)
				// row.text(note.content, 'tableText')
				const noteText =
					note.type === 'completion'
						? `Completion note: ${note.content}`
						: note.content
				row.text(this._wrapLongText(noteText), 'tableText')
			})

		return this
	}

	/**
	 * Add time of call and patient info as one table so all value column edges align.
	 *
	 * @return {CallPDF}
	 */
	_addTimeAndPatientInfoTable() {
		const table = this.pdf.table([100, '*']).noBorders()
		const timestamp = this.call.created_at
			? moment
					.utc(this.call.created_at)
					.local()
					.format('MM/DD/YYYY hh:mm A')
			: 'Unavailable'
		const phone = this.call.callback_number
			? util.newFormatPhoneNumberOMD(this.call.callback_number)
			: 'Unavailable'
		const provider = this.call.patient_provider
			? this.call.patient_provider.full_name
			: 'Unavailable'

		const rows = [
			['Received', timestamp],
			['Patient Name', this.call.patient_name || 'Unavailable'],
			['Provider', provider],
			['Phone Number', phone],
			['Patient DOB', this.call.patient_dob || 'Unavailable'],
			['Patient Message', this.call.patient_message || 'Unavailable'],
		]

		const cellMargin = [0, 4, 0, 4] // [left, top, right, bottom] – vertical gap between rows

		rows.forEach(([label, value]) => {
			const row = table.addRow()
			row.text(`${label}:`, 'label')
				.addStyles()
				.margin(cellMargin)
				.bold()
			row.text(value, 'body')
				.addStyles()
				.margin(cellMargin)
		})

		return this
	}

	/**
	 * Download pdf file.
	 *
	 * @return {void}
	 */
	_download() {
		this.pdf.download(this._getFileName())
	}

	/**
	 * Get the file name of the PDF.
	 *
	 * @return {String}
	 */
	_getFileName() {
		const timestamp = moment().format('YYYYMMDDHHmmss')

		return `ClarusCallSummary-${timestamp}.pdf`
	}

	/**
	 * Insert break opportunities in long unbroken runs so table text wraps.
	 * Uses zero-width space so the visible text is unchanged.
	 *
	 * @param {String} str
	 * @param {Number} maxRun Max characters in a run before inserting a break (default 35)
	 * @return {String}
	 */
	_wrapLongText(str, maxRun = 35) {
		if (typeof str !== 'string' || !str) return str
		const zwsp = '\u200B' // zero-width space
		let result = ''
		let run = 0
		for (let i = 0; i < str.length; i++) {
			const isSpace = /\s/.test(str[i])
			if (isSpace) {
				run = 0
				result += str[i]
			} else {
				run++
				if (run > maxRun) {
					result += zwsp
					run = 1
				}
				result += str[i]
			}
		}
		return result
	}

	/**
	 * Set the pdf's base styles.
	 *
	 * @return {void}
	 */
	_setBaseStyles() {
		this.pdf
			.defineStyle('header')
			.fontSize(12)
			.bold()
			.leftText()
			.margin([0, 0, 0, 6])

		this.pdf
			.defineStyle('headerExportLabel')
			.leftText()
			.margin([0, 0, 0, 0]) // spacing between lines

		this.pdf
			.defineStyle('body')
			.fontSize(12)
			.margin([0, 5])

		this.pdf
			.defineStyle('label')
			.fontSize(12)
			.bold()
		// .italics()

		this.pdf.defineStyle('sectionTitle').margin([0, 0, 0, 10]) // bottom margin

		this.pdf.defineStyle('tableText').fontSize(12)

		this.pdf.defineStyle('updateText').fontSize(12)
	}
}
